import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import {Button, Col, Divider, Form, message, Row, Space, Table} from 'antd';
import {AiFillEye} from 'react-icons/all';
import PractitionerSelect from '../../components/PractitionerSelect';
import SpecialitySelect from '../../components/SpecialitySelect';
import DiseaseSelect from '../../components/DiseaseSelect';

import {findMedicationRequests} from 'services/MedicationService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL, JSON_EXTRACT_EQUAL} from 'shared/constants/QueryModes';
import {periodTimeText} from 'utils/Utils';

const RequestList = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {patientId} = props;

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const [medications, setMedications] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    if (patientId?.length > 0) {
      const filters = [];

      filters.push({
        fields: 'request.patient.id',
        mode: EQUAL,
        value: patientId,
      });

      if (searchValues?.speciality?.id) {
        filters.push({
          fields: 'request.speciality',
          mode: JSON_EXTRACT_EQUAL,
          value: ['id', searchValues.speciality.id],
        });
      }

      if (searchValues?.disease?.id) {
        filters.push({
          fields: 'request.diagnostic',
          mode: JSON_EXTRACT_EQUAL,
          value: ['disease', 'id', searchValues.disease.id],
        });
      }

      if (searchValues?.requester) {
        filters.push({
          fields: 'requester.id',
          mode: EQUAL,
          value: searchValues.requester.id,
        });
      }

      setLoading(true);
      findMedicationRequests(headers, {filters})
        .then((res) => {
          setMedications(res.data);
          setPagination({
            ...params.pagination,
            total: res.headers[X_TOTAL_COUNT]
              ? parseInt(res.headers[X_TOTAL_COUNT])
              : 0,
          });
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadData({pagination});
  }, [patientId]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (item) => {
    navigate(`/health/medication-requests/${item?.id}`);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['request.speciality'],
      dataIndex: 'request',
      key: 'request',
      render: (request) => <span>{request?.speciality?.name}</span>,
    },
    {
      title: intl.messages['diagnostic.disease'],
      dataIndex: 'request',
      key: 'request',
      render: (request) => <span>{request?.diagnostic?.disease?.name}</span>,
    },
    {
      title: intl.messages['medication.requester'],
      dataIndex: 'requester',
      key: 'requester',
      render: (requester) => <span>{requester?.name}</span>,
    },
    {
      title: intl.messages['request.performanceDate'],
      dataIndex: 'request',
      key: 'request',
      render: (request) => (
        <span>
          {periodTimeText(
            request?.performanceStartTime,
            request?.performanceEndTime,
          )}
        </span>
      ),
    },
    {
      title: intl.messages['request.status'],
      dataIndex: 'request',
      key: 'request',
      sorter: true,
      render: (request) => (
        <span>
          {request?.status
            ? intl.messages[`request.status.${request.status.toLowerCase()}`]
            : ''}
        </span>
      ),
    },
    {
      title: intl.messages['common.actions'],
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right',
      render: (id, record) => (
        <Space>
          <Button
            type='primary'
            shape='circle'
            ghost
            icon={<AiFillEye />}
            onClick={() => onView(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      {patientId?.length > 0 && (
        <>
          <Form
            layout='vertical'
            form={form}
            initialValues={searchValues}
            onFinish={onSearch}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['speciality', 'id']}
                  label={intl.messages['request.speciality']}>
                  <SpecialitySelect
                    onChange={(value) => {
                      form.setFieldsValue({speciality: {id: value}});
                    }}
                    onClear={() => {
                      form.setFieldsValue({speciality: null});
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['disease', 'id']}
                  label={intl.messages['diagnostic.disease']}>
                  <DiseaseSelect
                    onChange={(value) => {
                      form.setFieldsValue({disease: {id: value}});
                    }}
                    onClear={() => {
                      form.setFieldsValue({disease: null});
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['requester', 'id']}
                  label={intl.messages['medication.requester']}>
                  <PractitionerSelect
                    onChange={(value) => {
                      form.setFieldsValue({requester: {id: value}});
                    }}
                    onClear={() => {
                      form.setFieldsValue({requester: null});
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={10} />
              <Col span={2}>
                <Button onClick={onClearSearch} style={{width: '100%'}}>
                  {intl.messages['common.reset']}
                </Button>
              </Col>
              <Col span={2}>
                <Button
                  type='primary'
                  htmlType='submit'
                  style={{width: '100%'}}>
                  {intl.messages['common.search']}
                </Button>
              </Col>
              <Col span={10} />
            </Row>
          </Form>

          <Divider />
        </>
      )}

      <Table
        loading={loading}
        columns={columns}
        dataSource={medications}
        rowKey={(record) => record.id}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default RequestList;

RequestList.propTypes = {
  patientId: PropTypes.string,
};
